// ============================================================================
// QUOTE ARTEFACT STAMP: the reference printed on a generated quote
// ----------------------------------------------------------------------------
// PATH A of Quote Quick Edit is a lookup, not a reconstruction: at PDF
// generation the quote is persisted verbatim into quote_artifacts (migration
// 085) and a short reference is stamped on the rendered page. Uploading that
// PDF later reads the reference back out of the text layer and loads the
// artefact exactly as it was sent. Fidelity is perfect by construction, and
// nothing is guessed.
//
// WHY A NEW REFERENCE AT ALL
// Neither existing identifier can key an artefact. projects.quote_number is the
// CLIENT's QR reference and is not unique (one value appears three times in the
// live book). projects.qm_quote_no is only allocated on send-quote-email, so a
// quote that was downloaded but never emailed has none. The artefact also has
// to outlive its project, so it cannot borrow a project-scoped identity.
//
// WHY THIS FORMAT, QMREF-A4XY-C7KP-M9TF
// The reference is printed on a document that goes to a paying customer and is
// read back out of a PDF text layer, so the format is driven by four
// constraints rather than by aesthetics:
//
//   1. UNAMBIGUOUS TO A HUMAN. The alphabet drops every character pair that is
//      confusable in print or when read aloud: 0/O, 1/I/L, 2/Z, 5/S, 8/B, U/V.
//      What survives is 19 letters and 5 digits. Rodney can read one off a
//      printed page and type it into the app without a second attempt.
//   2. NEVER MISTAKEN FOR A PRICE OR A DATE. There is no dollar sign, no
//      decimal point and no slash. Each group is LETTER, DIGIT, then two free
//      characters, so no group can ever be four digits (a year) and no group
//      can ever be four letters (a word). That second property is not pedantry:
//      a purely alphabetic group drawn at random can spell something the
//      operator would not want printed under his company name.
//   3. SURVIVES THE TEXT LAYER. The current template renders letter-spaced text
//      as one text item per glyph, so extraction returns "Q M R E F - A 4 X Y".
//      Every read therefore strips whitespace before matching, which is what
//      findQuoteRefInText does. The stamp additionally forces letter-spacing and
//      text-transform back to normal so the glyph run is as clean as it can be.
//   4. UNOBTRUSIVE. Twenty characters of small muted monospace in the footer.
//
// COLLISION MATHS. 19 x 5 x 24 x 24 combinations per group, three groups, is
// about 1.6e14, or roughly 47 bits. At a million artefacts the chance of any
// collision at all is under half a percent, and quote_artifacts.quote_ref
// carries a unique index, so the write path catches 23505 and re-mints. This is
// deliberately not a UUID: a UUID is not readable off a printed page.
//
// WHAT THE STAMP MUST NEVER CARRY. Only the reference. No quote data, no cost
// rates, no margins, no totals, no internal ids. The document is handed to the
// customer, and anything embedded in it is disclosed to the customer. Every
// internal detail lives behind the reference in the tenant-scoped artefact row,
// which is exactly why the reference exists. buildStampMarkup enforces this
// structurally: it accepts a reference and nothing else, and refuses anything
// that is not a well-formed reference, so there is no parameter through which
// quote data could reach the page.
// ============================================================================

/** Fixed prefix. Distinctive enough to find in a whitespace-stripped page. */
export const QUOTE_REF_PREFIX = 'QMREF';

/** Letters, minus I, L, O, U, S, B and Z (confusable with 1, 0, V, 5, 8, 2). */
export const QUOTE_REF_LETTERS = 'ACDEFGHJKMNPQRTVWXY';

/** Digits, minus 0, 1, 2, 5 and 8 (confusable with O, I/L, Z, S, B). */
export const QUOTE_REF_DIGITS = '34679';

/** The full symbol set used for the free positions in each group. */
export const QUOTE_REF_ALPHABET = QUOTE_REF_LETTERS + QUOTE_REF_DIGITS;

export const QUOTE_REF_GROUPS = 3;
export const QUOTE_REF_GROUP_LENGTH = 4;

/** Canonical length with the hyphens in: QMREF-XXXX-XXXX-XXXX. */
export const QUOTE_REF_CANONICAL_LENGTH =
    QUOTE_REF_PREFIX.length + QUOTE_REF_GROUPS * (QUOTE_REF_GROUP_LENGTH + 1);

/** Class on the stamp element, so the print clone and tests can find it. */
export const QUOTE_REF_STAMP_CLASS = 'qm-quote-ref-stamp';

/** Attribute carrying the reference, so a DOM consumer never has to re-parse text. */
export const QUOTE_REF_STAMP_ATTR = 'data-qm-quote-ref';

// Separator characters tolerated between the prefix and the groups. A PDF
// producer can emit a typographic dash rather than the ASCII hyphen we wrote,
// a line break can land mid-reference, and a hyphenation pass can inject a soft
// hyphen. Reading is forgiving; writing is always the canonical ASCII form.
const SEPARATORS = '\\s\\u00AD\\u2010-\\u2015\\u2212-';
const SEP_SRC = `[${SEPARATORS}]*`;

// LETTER, DIGIT, then two free symbols. See constraint 2 in the header.
const GROUP_SRC =
    `[${QUOTE_REF_LETTERS}][${QUOTE_REF_DIGITS}][${QUOTE_REF_ALPHABET}]{${QUOTE_REF_GROUP_LENGTH - 2}}`;

/**
 * The shared parse pattern, imported by the recovery slice.
 *
 * Deliberately NOT global: a module-level /g regex carries lastIndex between
 * calls, so two importers scanning different documents would corrupt each
 * other's results. Callers that need to scan build their own copy from
 * `.source`, which is what findQuoteRefInText does.
 *
 * Case-insensitive because a text layer can come back in either case, and
 * because an operator typing a reference by hand will not use capitals.
 */
export const QUOTE_REF_PATTERN: RegExp = new RegExp(
    `${QUOTE_REF_PREFIX}${SEP_SRC}${GROUP_SRC}${SEP_SRC}${GROUP_SRC}${SEP_SRC}${GROUP_SRC}`,
    'i',
);

// Characters removed before matching: all whitespace (which includes NBSP),
// the zero-width family, the BOM, and the soft hyphen. Per-glyph text items in
// the extracted layer are joined with spaces, so this is what puts the
// reference back together.
const STRIPPABLE = /[\s\u00AD\u200B-\u200D\uFEFF]+/g;

// Separators only, for canonicalisation. Kept apart from STRIPPABLE because
// the two do different jobs: one repairs the text layer, one normalises shape.
const SEPARATOR_CHARS = /[\u2010-\u2015\u2212-]/g;

/** Test seam only. Production always uses crypto; see randomBytesDefault. */
export interface MintQuoteRefOptions {
    randomBytes?: (count: number) => Uint8Array;
}

/**
 * Random bytes from the platform CSPRNG, falling back to Math.random only when
 * crypto is genuinely unavailable (an ancient WebView, or a stubbed test
 * environment). The fallback is weaker but still non-repeating, and the DB
 * unique index is the backstop either way.
 */
function randomBytesDefault(count: number): Uint8Array {
    const out = new Uint8Array(count);
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
            crypto.getRandomValues(out);
            return out;
        }
    } catch {
        // fall through to the pseudo-random path
    }
    for (let i = 0; i < count; i++) out[i] = Math.floor(Math.random() * 256);
    return out;
}

/**
 * Pull bytes in blocks rather than one call per character, so a single mint is
 * one CSPRNG call rather than twelve.
 */
function makeByteStream(source: (count: number) => Uint8Array): () => number {
    const BLOCK = 64;
    let buffer = source(BLOCK);
    let cursor = 0;
    return () => {
        if (cursor >= buffer.length) {
            buffer = source(BLOCK);
            cursor = 0;
            // A source that returns nothing must not hang the export.
            if (buffer.length === 0) return 0;
        }
        return buffer[cursor++] ?? 0;
    };
}

/**
 * One symbol, without modulo bias. A byte is 256 values; mapping it onto a
 * 24-symbol alphabet with a bare modulo would make the first 16 symbols more
 * likely, which quietly costs entropy. Values at or above the largest whole
 * multiple of the alphabet size are rejected and redrawn.
 *
 * The redraw is bounded: a stubbed source that always returns the same
 * out-of-range byte would otherwise spin forever. Once the guard trips we
 * accept a slightly biased symbol, because a biased reference is recoverable
 * and a hung PDF export is not.
 */
function pickSymbol(alphabet: string, nextByte: () => number): string {
    const size = alphabet.length;
    const ceiling = 256 - (256 % size);
    let byte = nextByte();
    for (let attempt = 0; byte >= ceiling && attempt < 64; attempt++) {
        byte = nextByte();
    }
    return alphabet.charAt(byte % size);
}

/**
 * Mint a fresh quote reference in canonical form, e.g. `QMREF-A4XY-C7KP-M9TF`.
 *
 * Pure: no DOM, no network, no persistence. The caller mints the reference
 * BEFORE the print HTML is built, because the render-pdf route receives HTML
 * that has already been rendered and so cannot allocate one.
 */
export function mintQuoteRef(options: MintQuoteRefOptions = {}): string {
    const nextByte = makeByteStream(options.randomBytes || randomBytesDefault);
    const groups: string[] = [];
    for (let g = 0; g < QUOTE_REF_GROUPS; g++) {
        let group = pickSymbol(QUOTE_REF_LETTERS, nextByte) + pickSymbol(QUOTE_REF_DIGITS, nextByte);
        while (group.length < QUOTE_REF_GROUP_LENGTH) {
            group += pickSymbol(QUOTE_REF_ALPHABET, nextByte);
        }
        groups.push(group);
    }
    return `${QUOTE_REF_PREFIX}-${groups.join('-')}`;
}

/**
 * Remove everything a PDF text layer can insert between glyphs. Run this before
 * matching: the current template splits letter-spaced runs into one text item
 * per glyph, and extraction joins those items with spaces.
 */
export function stripTextLayerWhitespace(text: string): string {
    if (typeof text !== 'string') return '';
    return text.replace(STRIPPABLE, '');
}

/**
 * Canonicalise a reference from any tolerated shape (spaced, unhyphenated,
 * lower case, typographic dashes) to `QMREF-XXXX-XXXX-XXXX`.
 *
 * Returns null rather than a best guess when the input is not a well-formed
 * reference. Nothing is silently defaulted: a reference we cannot read is
 * reported as unreadable so the operator sees the honest answer.
 */
export function normaliseQuoteRef(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;

    const compact = stripTextLayerWhitespace(raw)
        .replace(SEPARATOR_CHARS, '')
        .toUpperCase();

    const bodyLength = QUOTE_REF_GROUPS * QUOTE_REF_GROUP_LENGTH;
    if (compact.length !== QUOTE_REF_PREFIX.length + bodyLength) return null;
    if (compact.slice(0, QUOTE_REF_PREFIX.length) !== QUOTE_REF_PREFIX) return null;

    const body = compact.slice(QUOTE_REF_PREFIX.length);
    const groups: string[] = [];
    for (let g = 0; g < QUOTE_REF_GROUPS; g++) {
        const group = body.slice(g * QUOTE_REF_GROUP_LENGTH, (g + 1) * QUOTE_REF_GROUP_LENGTH);
        // Re-validate character by character rather than trusting the pattern:
        // this is the gate buildStampMarkup relies on to guarantee the markup
        // can only ever contain [A-Z0-9-].
        if (QUOTE_REF_LETTERS.indexOf(group.charAt(0)) === -1) return null;
        if (QUOTE_REF_DIGITS.indexOf(group.charAt(1)) === -1) return null;
        for (let i = 2; i < QUOTE_REF_GROUP_LENGTH; i++) {
            if (QUOTE_REF_ALPHABET.indexOf(group.charAt(i)) === -1) return null;
        }
        groups.push(group);
    }
    return `${QUOTE_REF_PREFIX}-${groups.join('-')}`;
}

/** True only for a reference already in exact canonical form. */
export function isValidQuoteRef(value: unknown): boolean {
    return typeof value === 'string' && normaliseQuoteRef(value) === value;
}

/**
 * Every DISTINCT stamped reference in extracted PDF text, in the order the
 * document presents them. Empty when there is no stamp at all, which is the
 * ordinary case for the back catalogue and routes the upload to PATH B.
 *
 * WHY THE FULL LIST AND NOT JUST THE FIRST. Builders merge quotes routinely:
 * two or three jobs for the same strata plan get combined into one PDF before
 * it goes out, and a scanned bundle can carry a whole month of quotes. Every
 * page of every one of them carries its own stamp. A reader that returns only
 * the first match cannot tell "one quote, stamped on each of its four pages"
 * from "two different quotes bound together", and those two cases need
 * opposite handling: the first recovers cleanly, the second must stop and ask
 * the operator which quote he is editing. Recovering the wrong one is the bad
 * outcome this exists to prevent, because the operator would then edit and
 * re-issue quote A believing it was quote B, and the customer receives it.
 *
 * De-duplicated, so a four-page quote returns ONE reference. Length greater
 * than one therefore means genuinely different quotes are present, which is a
 * fact the caller must surface rather than resolve on its own.
 *
 * Considers every candidate rather than stopping at the first parse: stripping
 * whitespace glues the reference to its neighbouring words, so an earlier
 * near-match elsewhere on the page must not shadow a real stamp.
 */
export function findAllQuoteRefsInText(text: unknown): string[] {
    if (typeof text !== 'string' || text.length === 0) return [];
    const compact = stripTextLayerWhitespace(text);
    if (compact.length === 0) return [];

    // A fresh global copy per call: the shared pattern stays non-global so no
    // lastIndex state can leak between callers.
    const candidates = compact.match(new RegExp(QUOTE_REF_PATTERN.source, 'gi'));
    if (!candidates) return [];

    // A Set preserves insertion order, so the result stays in document order
    // while the repeated per-page footer stamp collapses to one entry.
    const found = new Set<string>();
    for (const candidate of candidates) {
        const canonical = normaliseQuoteRef(candidate);
        if (canonical) found.add(canonical);
    }
    return Array.from(found);
}

/**
 * The FIRST stamped reference in extracted PDF text, or null if there is none.
 *
 * CAUTION, and the reason findAllQuoteRefsInText exists: this cannot tell a
 * merged document from a single one. If a PDF carries two different stamps it
 * returns the earlier of them with no signal that the other is there. Any
 * caller on the recovery path, where picking the wrong quote means re-issuing
 * the wrong document to a paying customer, must call findAllQuoteRefsInText
 * and handle a length greater than one explicitly. This narrower form is kept
 * for callers that only need to answer "is this document stamped at all".
 */
export function findQuoteRefInText(text: unknown): string | null {
    return findAllQuoteRefsInText(text)[0] ?? null;
}

// Inline rather than a stylesheet rule because the print document is assembled
// as a standalone HTML string and inline styles are the only thing guaranteed
// to survive it. letter-spacing and text-transform are forced back to normal
// because the quote template applies both to surrounding chrome, and either one
// corrupts what comes back out of the text layer. The colour reads the semantic
// muted-text token, with a neutral fallback for the standalone document.
const STAMP_STYLE = [
    'font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
    'font-size:8px',
    'line-height:1.3',
    'font-weight:600',
    'color:var(--qm-text-muted,#6b7280)',
    'letter-spacing:normal!important',
    'word-spacing:normal!important',
    'text-transform:none!important',
    'font-variant-ligatures:none',
    'white-space:nowrap',
].join(';');

/**
 * The footer fragment that stamps the reference on the rendered document.
 *
 * Carries the reference and nothing else. The canonical form is drawn entirely
 * from [A-Z0-9-], so the interpolation below cannot inject markup and cannot
 * carry a payload; that is enforced by normalising first and refusing anything
 * that is not a well-formed reference, rather than by escaping. Refusing is the
 * right failure: a stamp that silently did not appear would send a quote the
 * app can never recover, which is the loss mode this whole path exists to fix.
 *
 * @throws when the reference is not readable as a quote reference.
 */
export function buildStampMarkup(ref: unknown): string {
    const canonical = normaliseQuoteRef(ref);
    if (!canonical) {
        throw new Error(
            'buildStampMarkup: refused to stamp a value that is not a quote reference. '
            + `Mint one with mintQuoteRef(); expected the shape ${QUOTE_REF_PREFIX}-XXXX-XXXX-XXXX.`,
        );
    }
    return `<div class="${QUOTE_REF_STAMP_CLASS}" ${QUOTE_REF_STAMP_ATTR}="${canonical}" style="${STAMP_STYLE}">${canonical}</div>`;
}
